import { useState } from "react";
import { LETTERS } from "../../qa";
import styles from "./NotePanel.module.css";

function timeAgo(ts) {
  const mins = Math.max(0, Math.round((Date.now() - ts) / 60000));
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / 1440)}d ago`;
}

export default function NotePanel({ question, notes, reviewer, onComment, onSuggest, onDelete }) {
  const [mode, setMode] = useState(null);
  const [comment, setComment] = useState("");
  const [draft, setDraft] = useState(null);

  function openSuggest() {
    setDraft({
      question: question.text,
      options: [...question.options],
      answerIndex: question.answerIndex,
      reason: "",
    });
    setMode("suggest");
  }

  function close() {
    setMode(null);
    setComment("");
    setDraft(null);
  }

  function submitComment(e) {
    e.preventDefault();
    if (!comment.trim()) return;
    onComment(question.id, comment.trim());
    close();
  }

  function submitSuggest(e) {
    e.preventDefault();
    const { reason, ...suggestion } = draft;
    onSuggest(question.id, suggestion, reason.trim());
    close();
  }

  const setOption = (i, value) =>
    setDraft((d) => ({ ...d, options: d.options.map((o, j) => (j === i ? value : o)) }));

  return (
    <div className={styles.panel}>
      <div className={styles.bar}>
        <button type="button" className={`${styles.btn} ${mode === "comment" ? styles.on : ""}`} onClick={() => (mode === "comment" ? close() : setMode("comment"))}>
          + Comment
        </button>
        <button type="button" className={`${styles.btn} ${mode === "suggest" ? styles.on : ""}`} onClick={() => (mode === "suggest" ? close() : openSuggest())}>
          ↻ Suggest replacement
        </button>
      </div>

      {mode === "comment" && (
        <form className={styles.form} onSubmit={submitComment}>
          <textarea
            className={styles.area}
            rows={3}
            maxLength={500}
            autoFocus
            value={comment}
            placeholder="WHAT IS WRONG OR UNCLEAR?"
            aria-label="Comment"
            onChange={(e) => setComment(e.target.value)}
          />
          <div className={styles.actions}>
            <button type="submit" className={`${styles.btn} ${styles.primary}`} disabled={!comment.trim()}>Post comment</button>
            <button type="button" className={styles.btn} onClick={close}>Cancel</button>
          </div>
        </form>
      )}

      {mode === "suggest" && draft && (
        <form className={styles.form} onSubmit={submitSuggest}>
          <label className={styles.field}>
            <span className={styles.label}>Replacement question</span>
            <textarea
              className={styles.area}
              rows={3}
              maxLength={400}
              value={draft.question}
              onChange={(e) => setDraft((d) => ({ ...d, question: e.target.value }))}
            />
          </label>
          <fieldset className={styles.options}>
            <legend className={styles.label}>Options (select the correct one)</legend>
            {draft.options.map((option, i) => (
              <div key={LETTERS[i]} className={styles.optionRow}>
                <label className={styles.radio}>
                  <input
                    type="radio"
                    name={`correct-${question.id}`}
                    checked={draft.answerIndex === i}
                    onChange={() => setDraft((d) => ({ ...d, answerIndex: i }))}
                  />
                  <span className={styles.radioLetter}>{LETTERS[i]}</span>
                </label>
                <input
                  className={styles.text}
                  value={option}
                  maxLength={160}
                  aria-label={`Option ${LETTERS[i]}`}
                  onChange={(e) => setOption(i, e.target.value)}
                />
              </div>
            ))}
          </fieldset>
          <label className={styles.field}>
            <span className={styles.label}>Why? (optional)</span>
            <input
              className={styles.text}
              value={draft.reason}
              maxLength={500}
              onChange={(e) => setDraft((d) => ({ ...d, reason: e.target.value }))}
            />
          </label>
          <div className={styles.actions}>
            <button
              type="submit"
              className={`${styles.btn} ${styles.primary}`}
              disabled={!draft.question.trim() || draft.options.some((o) => !o.trim())}
            >
              Submit suggestion
            </button>
            <button type="button" className={styles.btn} onClick={close}>Cancel</button>
          </div>
        </form>
      )}

      {notes.length > 0 && (
        <ul className={styles.list}>
          {notes.map((note) => (
            <li key={note.id} className={`${styles.note} ${note.kind === "suggest" ? styles.suggest : ""}`}>
              <p className={styles.noteHead}>
                <span>{note.kind === "suggest" ? "Suggestion" : "Comment"} / {note.reviewer}</span>
                <span>{timeAgo(note.ts)}</span>
              </p>
              {note.text && <p className={styles.noteText}>{note.text}</p>}
              {note.suggestion && (
                <div className={styles.proposal}>
                  <p className={styles.proposalQ}>{note.suggestion.question}</p>
                  <ol className={styles.proposalOpts} type="A">
                    {note.suggestion.options.map((o, i) => (
                      <li key={LETTERS[i]} className={i === note.suggestion.answerIndex ? styles.key : ""}>
                        {o}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
              {note.reviewer === reviewer && (
                <button type="button" className={styles.delete} onClick={() => onDelete(question.id, note.id)}>
                  Delete
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
